/**
 * 结论总览：六张可点击筛选卡 + 分段占比条 + 强事件计数标签。
 * 点击卡片 / 强事件标签只改父级的筛选状态，真正的取数在明细表里。
 */
import { Tooltip } from 'antd';
import type { BehaviorCompareSummary, BehaviorLabel } from '@/apis/simulation';

import {
  LABEL_BAR_COLOR,
  LABEL_META,
  LABEL_ORDER,
  STRONG_EVENT_NAMES,
  buildBarSegments,
  formatCount,
  formatDelta,
  formatPercent,
} from '../format';

interface Props {
  summary: BehaviorCompareSummary | null;
  activeLabel?: BehaviorLabel;
  activeStrong?: string;
  onLabel: (label?: BehaviorLabel) => void;
  onStrong: (code?: string) => void;
}

export default function OverviewCards({ summary, activeLabel, activeStrong, onLabel, onStrong }: Props) {
  const counts = summary?.labelCounts ?? {};
  const total = summary?.commonSceneNum ?? 0;
  const segments = buildBarSegments(counts);
  const strong = Object.entries(summary?.strongCounts ?? {}).sort((a, b) => b[1] - a[1]);
  const delta = summary?.prevReviewDelta;
  // 只有翻转场景算完时：计数只含已算部分，占比条与「占共 N 的百分比」都会误导，所以不画条、不写百分比
  const partial = !!summary?.partial;
  const computed = LABEL_ORDER.reduce((n, l) => n + (counts[l] ?? 0), 0);

  return (
    <div className="bc-card">
      <div className="bc-card-title">
        结论总览<span className="bc-card-hint">（点击卡片筛选下方列表）</span>
        {partial ? <span className="bc-card-hint" style={{ color: '#d46b08' }}>　部分结果：先算了两侧结论不同的场景，其余场景还在计算</span> : null}
      </div>
      <div className="bc-tiles">
        <button
          type="button"
          className={`bc-tile ${activeLabel ? '' : 'bc-tile--active'}`}
          onClick={() => onLabel(undefined)}
        >
          <div className="bc-tile-name">{partial ? '已计算场景' : '可比较场景'}</div>
          <div className="bc-tile-num">{formatCount(partial ? computed : summary?.comparableNum)}</div>
          <div className="bc-tile-sub">
            {partial ? `共同场景 ${formatCount((summary?.flippedTotal ?? 0) + (summary?.otherTotal ?? 0))}` : `共 ${formatCount(total)}`}
          </div>
        </button>
        {LABEL_ORDER.map((label) => {
          const meta = LABEL_META[label];
          const n = counts[label] ?? 0;
          return (
            <button
              type="button"
              key={label}
              className={`bc-tile ${activeLabel === label ? 'bc-tile--active' : ''}`}
              onClick={() => onLabel(activeLabel === label ? undefined : label)}
            >
              <div className="bc-tile-name">
                <i className="bc-dot" style={{ background: LABEL_BAR_COLOR[label] }} />
                <span style={{ color: meta.color }}>{meta.icon}</span> {meta.name}
              </div>
              <div className="bc-tile-num">{formatCount(n)}</div>
              <div className="bc-tile-sub">{partial ? '仅已算部分' : formatPercent(n, total)}</div>
            </button>
          );
        })}
        <div className="bc-tile bc-tile--static">
          <div className="bc-tile-name">较上次对比</div>
          {summary?.prevCompareId ? (
            <>
              <div className="bc-tile-num bc-tile-num--sm">需复核 {formatDelta(delta)}</div>
              <div className="bc-tile-sub">vs 对比 #{summary.prevCompareId}</div>
            </>
          ) : (
            <>
              <div className="bc-tile-num bc-tile-num--sm">首次对比</div>
              <div className="bc-tile-sub">暂无上一次可比</div>
            </>
          )}
        </div>
      </div>

      {partial ? null : <div className="bc-bar">
        {segments.map((s) => (
          <Tooltip key={s.label} title={`${LABEL_META[s.label].name} ${formatCount(s.count)}（${formatPercent(s.count, total)}）`}>
            <div className="bc-bar-seg" style={{ width: `${s.widthPct}%`, background: LABEL_BAR_COLOR[s.label] }} />
          </Tooltip>
        ))}
      </div>}

      <div className="bc-strong">
        <span className="bc-strong-title">强事件：</span>
        {strong.length === 0 ? <span className="bc-strong-empty">无</span> : null}
        {strong.map(([code, n]) => (
          <button
            type="button"
            key={code}
            className={`bc-strong-tag ${activeStrong === code ? 'bc-strong-tag--active' : ''}`}
            onClick={() => onStrong(activeStrong === code ? undefined : code)}
          >
            {STRONG_EVENT_NAMES[code] ?? code} · {n}
          </button>
        ))}
        {summary && summary.verdictMismatchNum > 0 ? (
          <span className="bc-strong-note">另有 {summary.verdictMismatchNum} 个场景两侧平台结论不一致</span>
        ) : null}
      </div>
    </div>
  );
}
